import React from 'react';

import InputWrapper from '../InputWrapper';
import DropDown from 'components/common/dropDown';
import { INITIAL_EMPLOYEE_SELECT_VALUE } from 'constants/appConstants';

interface SelectOption {
  value: string | number;
  label?: string;
  [key: string]: string | number | boolean | null | undefined;
}

type SelectValue = string | number | Array<string | number> | null | Record<string, never>;

interface Props {
  label?: string;
  error?: React.ReactNode;
  isMandatory?: boolean;
  options: SelectOption[];
  name: string;
  onChange?: (event: { target: { name: string; value: SelectValue } }) => void;
  onBlur?: (event: { target: { name: string } }) => void;
  value?: SelectValue;
  isMulti?: boolean;
  isDisabled?: boolean;
  onInputChange?: (inputText: string) => void;
  noOptionsMessage?: () => React.ReactNode;
  classNamePrefix?: string;
  className?: string;
  formatOptionLabel?: (option: SelectOption) => React.ReactNode;
  isClearable?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  formatCreateLabel?: (inputValue: string) => React.ReactNode;
}

const FormDropdown: React.FC<Props> = (props) => {
  const {
    label = '',
    error,
    isMandatory,
    options,
    name,
    onChange,
    onBlur,
    value,
    isMulti,
    isDisabled,
    onInputChange,
    noOptionsMessage,
    classNamePrefix = 'lf-select',
    className,
    formatOptionLabel,
    isClearable,
    ...rest
  } = props;

  const onDropDownChange = (item: SelectOption | SelectOption[] | null) => {
    if (!onChange) {
      return null;
    }

    let value;

    if (item === null) {
      value = null;
    } else {
      value = isMulti ? (item as SelectOption[]).map(({ value }) => value) : (item as SelectOption).value;
    }

    return onChange({
      target: {
        name,
        value,
      },
    });
  };

  const onDropDownBlur = () => {
    if (!onBlur) {
      return null;
    }

    return onBlur({
      target: {
        name,
      },
    });
  };

  const getValueFromOptions = (): SelectOption | SelectOption[] | null | undefined => {
    if (!isMulti) {
      if (value === null) {
        return null;
      }

      return options.find(({ value: optValue }) => optValue === value);
    }

    const selectedValues = Array.isArray(value) ? value : [];
    return options.filter(({ value: optValue }) => selectedValues.includes(optValue));
  };

  const isInitialSelectValue =
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    value === INITIAL_EMPLOYEE_SELECT_VALUE.value;

  return (
    <InputWrapper label={label} error={error} isMandatory={isMandatory}>
      <DropDown
        isBig
        autoResize={true}
        hasError={Boolean(error)}
        isSearchable={true}
        isDisabled={isDisabled}
        options={options}
        onDropDownChange={onDropDownChange}
        onBlur={onDropDownBlur}
        isMulti={isMulti}
        value={isInitialSelectValue ? undefined : getValueFromOptions()}
        onInputChange={onInputChange}
        noOptionsMessage={noOptionsMessage}
        formatOptionLabel={formatOptionLabel}
        classNamePrefix={classNamePrefix}
        customClassName={className}
        isClearable={isClearable}
        {...rest}
      />
    </InputWrapper>
  );
};

export default FormDropdown;
